import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { LoginRequest, LoginResponse, UserInfo } from '../types/auth.types';
import * as authApi from '../api/authApi';
import { clearTokens, getRefreshToken, setAccessToken, setRefreshToken } from '../api/tokenStore';

const USER_STORAGE_KEY = 'sclab_user';

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  login: (credentials: LoginRequest) => Promise<UserInfo>;
  logout: () => Promise<void>;
  updateToken: (accessToken: string, refreshToken?: string) => void;
  setUser: (user: UserInfo | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function readStoredUser(): UserInfo | null {
  const raw = localStorage.getItem(USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
}

// Cliente sin interceptores para no activar la lógica de refresh automático
// durante la inicialización de la sesión.
const plainAxios = axios.create({ baseURL: process.env.REACT_APP_API_URL });

export function AuthProvider({ children }: { children: ReactNode }) {
  const hasStoredSession = readStoredUser() !== null && getRefreshToken() !== null;

  const [user, setUserState] = useState<UserInfo | null>(readStoredUser);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(hasStoredSession);
  const [isInitializing, setIsInitializing] = useState<boolean>(hasStoredSession);

  // Evita que el efecto corra dos veces en React StrictMode (desarrollo).
  const initCalled = useRef(false);

  const setUser = useCallback((nextUser: UserInfo | null) => {
    setUserState(nextUser);
    if (nextUser) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (initCalled.current) return;
    initCalled.current = true;

    const storedRefresh = getRefreshToken();
    if (!storedRefresh) {
      setIsInitializing(false);
      return;
    }

    plainAxios
      .post<LoginResponse>('/api/auth/refresh', { refreshToken: storedRefresh })
      .then((res) => {
        setAccessToken(res.data.accessToken);
        setRefreshToken(res.data.refreshToken);
        setUser(res.data.user);
        setIsAuthenticated(true);
      })
      .catch(() => {
        clearTokens();
        localStorage.removeItem(USER_STORAGE_KEY);
        setIsAuthenticated(false);
        setUserState(null);
      })
      .finally(() => {
        setIsInitializing(false);
      });
  }, [setUser]);

  const updateToken = useCallback((accessToken: string, refreshToken?: string) => {
    setAccessToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken);
    setIsAuthenticated(true);
  }, []);

  const login = useCallback(
    async (credentials: LoginRequest): Promise<UserInfo> => {
      const response = await authApi.login(credentials);
      updateToken(response.accessToken, response.refreshToken);
      setUser(response.user);
      return response.user;
    },
    [setUser, updateToken]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clearTokens();
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [setUser]);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isInitializing, login, logout, updateToken, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de un AuthProvider');
  return ctx;
}
